import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import _ from 'lodash';
import { FlexibleObj } from './interfaces/FlexibleObj';
import { UICategory, UILoggerSingleton } from './logger/UILoggerSingleton';

declare var window;

@supertypeClass({ toClient: true, toServer: false })
export class MetadataNode extends Everything {
  @property()
  id: string;

  @property()
  prop: string;
  /* This a list of all the data model properties associated with a custom control*/

  @property({ type: Array, getType: () => String })
  customProps: Array<string>;
  /* This a list of properties associated with a custom control which are required (marked with 'yes')*/

  @property({ type: Array, getType: () => String })
  requiredProps: Array<string>;

  @property()
  styleClass: string;

  @property()
  subtext: string;

  @property()
  otherProp: string;

  @property()
  entity: string;

  @property()
  inNestedEntity: boolean;

  @property()
  qText: string;

  @property()
  qTranslate: string;

  @property()
  subProperty: string;

  @property()
  pronounLocked: boolean;

  @property()
  validation: string;

  @property()
  level: number = 1;
  /* level from meta data, used to establish parent child*/

  @property()
  uiLevel: number;
  /* level for UI (may not be the same as level in certain cases)*/

  @property()
  showifExp: string;
  /* Show this node when the parent value is this*/

  @property()
  isShowing: boolean;
  /* Is the node currently showing?*/

  @property()
  controlType: string;
  /* radiogroup, text, autofill, label, checkbox, date, shortdate or custom*/

  @property()
  type: string;
  /* type of Property - multivalue, boolean, string, array*/

  @property()
  arrayType: string;
  /* If the type is Array, which type of Array?*/

  @property()
  section: string;
  /* Name of the section if this is a top level node*/

  @property()
  topLevel: boolean;
  /* Containing sections are top level*/

  @property()
  optional: boolean;
  /* If the control is optional or mandatory*/

  @property()
  widths: string;
  /* Mostly applies to radiogroup*/

  @property()
  placeholder: string;

  @property()
  helpText: string;

  @property()
  fid: string;
  /* A unique string (with no periods) needed for autofill controls*/

  @property()
  layout: string;
  /* Should this control be in its column?*/

  @property()
  width: string;
  /* width of the column in terms of bootstrap's 12 column grid*/

  @property()
  parentLevel1NodeId: string;
  /* The top level node this node is contained within*/

  @property()
  loadingCondition: string;
  /* If the control shows a loading icon, condition when it shows it?*/

  @property({ type: Array, getType: () => String })
  relatedQuestions: Array<string>;

  @property({ type: Array, getType: () => String })
  widthsArray: Array<string>;

  @property()
  iteratorMaxLength: number;

  @property()
  sectionOnEnter: string;

  @property()
  parent: MetadataNode;

  @property({ type: Array, getType: () => MetadataNode })
  children: Array<MetadataNode> = [];

  @property()
  permutation;

  @property()
  permutationAnswers: FlexibleObj<Array<string>>;

  @property()
  placeHolder: string;

  @property()
  loadingConditionMessage: string;

  constructor() {
    super();
  }

  static new(prop: string, options?): MetadataNode {
    return new MetadataNode().init(prop, options);
  }

  init(prop: string, options?): this {
    this.prop = prop;

    const opts = options || {};

    if (prop) {
      const tokens = prop.split('.');

      tokens.pop();

      this.entity = tokens.join('.');
    }

    this.inNestedEntity = !!opts.inNestedEntity;
    this.styleClass = opts.styleClass;
    this.placeholder = opts.placeholder;
    this.customProps = opts.customProps ? opts.customProps.split(',') : null;
    this.pronounLocked = !!opts.pronounLocked;
    this.section = opts.section;
    this.id = opts.id;
    this.qText = this.id !== '0001' ? this.replaceNewLines(this.substitutePronouns(opts.qText)) : opts.qText;
    this.qTranslate = opts.qTranslate;
    this.permutation = opts.permutation;
    this.permutationAnswers = opts.permutationAnswers;
    this.showifExp = opts.showifExp;
    this.otherProp = opts.otherProp;
    this.controlType = opts.controlType;
    this.type = opts.type;
    this.subtext = opts.subtext;

    if (this.type) {
      this.arrayType = this.type.match(/array *of *([A-Za-z]*)/) ? RegExp.$1 : '';
    }

    this.section = opts.section;
    this.topLevel = !!this.section;

    if (!this.topLevel) {
      this.level = Number(opts.level);
      this.uiLevel = opts.uiLevel >= 0 ? Number(opts.uiLevel) : this.level - 1;
    }

    this.optional = opts.required === 'no' || opts.required === 'No';
    this.requiredProps = opts.required ? opts.required.split(',') : null;
    this.widths = opts.widths ? opts.widths : '[2,4,6]';
    this.widthsArray = JSON.parse(this.widths);
    this.placeHolder = opts.placeHolder;
    this.helpText = opts.helpText;
    this.fid = opts.fid ? opts.fid : this.buildFid(this.prop);
    this.layout = opts.layout;
    this.width = opts.width;
    this.subProperty = opts.subProperty;
    this.loadingCondition = opts.loadingCondition;
    this.loadingConditionMessage = opts.loadingConditionMessage;
    this.validation = this.convertToValidationExpression(opts.validation);
    this.parentLevel1NodeId = this.id;
    this.relatedQuestions = opts.relatedQuestions ? opts.relatedQuestions.split(',') : null;
    this.iteratorMaxLength = opts.iteratorMaxLength ? Number(opts.iteratorMaxLength) : 10;
    this.sectionOnEnter = opts.sectionOnEnter;

    return this;
  }

  substitutePronouns(text: string): string {
    if (this.pronounLocked) {
      return text;
    }

    if (text) {
      // Uses markdown-style syntax for links [link text](href)
      text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/, '<a href="$2" target="_blank">$1</a>');
      // Uses markdown-style syntax for modals [modal text][modal function call]
      text = text.replace(/\[([^\]]+)\]\[([^*]+)\]/, '<a b:onclick="$2">$1</a>');
      text = text.replace(/\bDo you\b/, '<b:doyou></b:doyou>');
      text = text.replace(/\bHave you\b/, '<b:haveyou></b:haveyou>');
      text = text.replace(/\bWere you\b/, '<b:wereyou></b:wereyou>');
      text = text.replace(/\bAre you\b/, '<b:areyou></b:areyou>');
      text = text.replace(/\bdo you\b/, '<b:doyoulc></b:doyoulc>');
      text = text.replace(/\bhave you\b/, '<b:haveyoulc></b:haveyoulc>');
      text = text.replace(/\bwere you\b/, '<b:wereyoulc></b:wereyoulc>');
      text = text.replace(/\bare you\b/, '<b:areyoulc></b:areyoulc>');
      text = text.replace(/\byou are\b/, '<b:youare></b:youare>');
      text = text.replace(/\byou\b/, '<b:you></b:you>');
      text = text.replace(/\byour\b/, '<b:your></b:your>');
      text = text.replace(/\bYour\b/, '<b:youruc></b:youruc>');
      text = text.replace(/\bthe owner dies\b/, '<b:ownerdies></b:ownerdies>');
      text = text.replace(/\bthe owner's\b/, '<b:owneryour></b:owneryour>');
      text = text.replace(/\bthe owner\b/, '<b:owneryou></b:owneryou>');
      text = text.replace(/\b__household__\b/, '<b:household></b:household>');
      text = text.replace(/\b__Household__\b/, '<b:householduc></b:householduc>');
      text = text.replace(/\b__householdEarned__\b/, '<b:householdEarned></b:householdEarned>');

      // Special treatment for your. If there is a pronoun before the "your", replace your with
      // their
      const yourIndex = text.indexOf('<b:your></b:your>');

      if (yourIndex > 0) {
        // Is there a preceding pronoun?
        const labelSub = text.substr(0, yourIndex + 1);

        if (labelSub.match(/b:/)) {
          text = text.replace(/<b:your><\/b:your>/g, '<b:their></b:their>');
        }
      }
    }

    return text;
  }

  replaceNewLines(text: string): string {
    return text ? text.replace(/[\r\n]/gm, '<br/><div class="app-indent"></div>') : '';
  }

  buildFid(prop?: string): string {
    return (this.id + this.prop).replace(/[\.\[\]]/g, '_');
  }

  updateFid(): void {
    this.fid = this.buildFid();
  }

  convertToValidationExpression(validationData): string {
    if (!validationData) {
      return ';';
    }

    // As this turns off all validation (including validators on the model property)
    return validationData;
  }

  addChild(node: MetadataNode, parentId: string): MetadataNode {
    this.children.push(node);

    node.parent = this;
    node.parentLevel1NodeId = parentId;

    return node;
  }

  shallowCopy(): MetadataNode {
    const newNode = MetadataNode.new(this.prop);

    newNode.entity = this.entity;
    newNode.inNestedEntity = this.inNestedEntity;
    newNode.customProps = this.customProps;
    newNode.requiredProps = this.requiredProps;
    newNode.qText = this.qText;
    newNode.showifExp = this.showifExp;
    newNode.controlType = this.controlType;
    newNode.type = this.type;
    newNode.arrayType = this.arrayType;
    newNode.section = this.section;
    newNode.topLevel = this.topLevel;
    newNode.optional = this.optional;
    newNode.widths = this.widths;
    newNode.placeHolder = this.placeHolder;
    newNode.helpText = this.helpText;
    newNode.fid = this.fid;
    newNode.parent = this.parent;
    newNode.children = this.children;
    newNode.layout = this.layout;
    newNode.width = this.width;
    newNode.loadingCondition = this.loadingCondition;
    newNode.loadingConditionMessage = this.loadingConditionMessage;
    newNode.sectionOnEnter = this.sectionOnEnter;
    newNode.resetLevel();

    return newNode;
  }

  setProp(prop: string): void {
    this.prop = prop;
    this.fid = this.buildFid();
  }

  isPropMatch(prop: string): boolean {
    if (this.prop === prop) {
      return true;
    } else if (this.customProps) {
      for (let i = 0; i < this.customProps.length; i++) {
        if (this.prop + '.' + this.customProps[i] === prop) {
          return true;
        }
      }
    }

    return false;
  }

  isShown(): boolean {
    let parentIsPresent = true;

    // Hate special handling
    if (
      (this.parent.controlType && this.parent.controlType.match(/iterate-collection/)) ||
      (this.parent.type && !this.parent.type.match(/boolean|string|number|multivalue|date/i))
    ) {
      parentIsPresent = this.parent.isShown();
    }

    if (this.showifExp) {
      // eval in a nested scope
      try {
        const willBeShown: boolean = function () {
          // Necessary eval that evaluates the configuration in the metadata
          //tslint:disable-next-line
          return parentIsPresent && eval(this.showifExp);
        }.bind(this)(); // this is how we bind the context to the eval
        if (willBeShown) {
          if (this.type === 'DiagnosisShort') {
            //tslint:disable-next-line
            let prop = eval('controller.' + this.prop);
            if (!prop) {
              //tslint:disable-next-line
              eval(
                'controller.' +
                  this.prop +
                  ' = ' +
                  'RemoteObjectTemplate.__dictionary__.' +
                  this.type +
                  '.new(controller.customer)'
              );
            }
          }
        } else {
          if (this.type === 'DiagnosisShort') {
            //tslint:disable-next-line
            let prop = eval('controller.' + this.prop);
            if (!prop) {
              //tslint:disable-next-line
              eval('controller.' + this.prop + ' = null');
            }
          }
        }
        return willBeShown;
      } catch (err) {
        const error = {
          isHumanRelated: false,
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
        };
        UILoggerSingleton.getInstance().error({
          module: 'MetadataNode',
          function: 'isShown',
          category: UICategory.security,
          error: error,
        });

        return false;
      }
    } else {
      return parentIsPresent;
    }
  }

  isValid(errorMessages?: string): boolean {
    /** There are three scenarios of how to validate -
     * 1. If all the immediate children of the current node are checkboxes, check if at least
     *    one of them is selected and validate the selected checkbox and its children.
     * 2. If there are conditional Error messages showing, associated with this node, then
     *    return false.
     * 3. Otherwise, validate the current node and its children.
     */
    if (!this.isShown()) {
      return true;
    }

    if (errorMessages && errorMessages[this.id] && errorMessages[this.id].length) {
      return false;
    }

    if (this.validateValue()) {
      // Validate children
      if (this.children.length > 0) {
        // See if all children are checkboxes and validate if at least
        // one is selected
        // Check if there is None node or Other/Not sure node in the children list.
        const lastNode = this.children[this.children.length - 1];

        if (lastNode instanceof NoneNode || lastNode instanceof OtherNotSureNode) {
          // If the checkboxes are not displaying, no need to validate
          if (!lastNode.isShown()) {
            return true;
          }

          // Find all the check boxes which have been checked
          const cbsChecked = this.children.filter(function (item) {
            return item.controlType === 'checkbox-basic' && item.validateValue();
          });

          // If there is at least one checkbox which is valid...
          if (cbsChecked.length > 0) {
            return cbsChecked.every(function (item) {
              return item.isValid(errorMessages);
            });
          }

          return false;
        } else {
          // If we are dealing with an "iterate" control, special handling
          // is required
          if (this.controlType && this.controlType.match(/iterate-collection/)) {
            return this.validateIterate();
          }

          return this.children.every(function (item) {
            return item.isValid(errorMessages);
          });
        }
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  validateValue(): boolean {
    if (this.controlType && this.controlType.match(/^label/)) {
      // Labels don't take a value
      return true;
    }

    const propName = this.prop,
      customProps = this.customProps;

    if (propName && !this.optional) {
      if (customProps) {
        return customProps.every(
          function (prop, i) {
            if (!this.requiredProps || this.requiredProps[i] === 'yes') {
              const customPropName = propName + '.' + prop;

              return this.hasValue(customPropName);
            } else {
              return true;
            }
          }.bind(this)
        );
      } else {
        return this.hasValue(propName);
      }
    }

    return true;
  }

  hasValue(propName: string): boolean {
    // false is a valid value for radiogroups,
    // 0 is valid value for Numbers
    let isValid;

    try {
      // Necessary Eval to check if radio group has value
      isValid =
        this.controlType.indexOf('radio-group') === 0
          ? //tslint:disable-next-line
            eval(
              'controller.' + propName + ' != null && typeof controller.' + propName + " != 'undefined' ? true : false"
            )
          : //tslint:disable-next-line
            eval('controller.' + propName + ' === 0 || controller.' + propName + '? true : false');
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'MetadataNode',
        function: 'hasValue',
        category: UICategory.security,
        error: error,
      });

      isValid = false;
    }

    return isValid;
  }

  isAnyChildChecked(): boolean {
    const children = this.children;

    for (const childCheckbox in children) {
      if (children[childCheckbox].prop !== '' && children[childCheckbox].hasValue(children[childCheckbox].prop)) {
        return true;
      }
    }

    return false;
  }

  validateIterate(): boolean {
    // For each element in the array, get the corresponding node from the view model,
    // if the node is shown, make sure each element has a value
    const arrayProp = this.prop, // Needed for RegExp.$x
      match = this.type.match(/array *of *([A-Za-z]*)/), // eslint-disable-line
      arrayType = RegExp.$1;

    // Eval node property passed by string (e.g. metadata)
    //tslint:disable-next-line
    var arrayValues = eval('controller.' + arrayProp);

    if (arrayValues.length === 0) {
      return true;
    }

    return arrayValues.every(
      function (arrayVal) {
        const props = arrayVal.amorphicClass.getProperties();

        // Used to evaluate the current iterate type and instantiate a new one (e.g. from metadata)
        //tslint:disable-next-line
        var tempCondition = eval(
          'controller.current' + arrayType + ' = RemoteObjectTemplate.__dictionary__.' + arrayType + '.new()'
        );

        for (const prop in props) {
          // Check if the prop is being shown and if so validate it
          const defineProp = props[prop];

          if (!prop.match(/__/) && !defineProp.isLocal) {
            tempCondition[prop] = arrayVal[prop];
          }
        }

        return this.children.every(function (item) {
          return item.isValid();
        });
      }.bind(this)
    );
  }

  /**
   * Verify if the prop is pointing to a property that exists or not.
   *
   * For e.g. customer.application.primaryPolicy.foo[0].bar is pointing to
   * an incorrect property if customer.application.primaryPolicy.foo is null
   *
   * @returns {Boolean}
   */

  isPropNonNull(): boolean {
    if (!this.prop) {
      return false;
    }

    // Split the propName into tokens and check for the existence of each token
    const tokens = this.prop.split('.');
    let entity = window.controller;

    return tokens.every(function (token) {
      if (entity && typeof entity[token] !== 'undefined') {
        entity = entity[token];

        return true;
      } else {
        return false;
      }
    });
  }

  /* Go through all children recursively and reset their underlying model properties*/

  resetChildren(): void {
    this.children.forEach(function (node) {
      node.resetVal();

      //tslint:disable-next-line
      !(node.controlType && node.controlType.match(/iterate-collection/)) ? node.resetChildren() : '';
    });
  }

  resetVal(): void {
    // If this is a node with no prop or the prop does not exist, there is nothing to reset
    if (!this.isPropNonNull()) {
      return;
    }

    // If the prop is part of a nested entity, the parent trigger would have
    // already cleared out this prop's value.
    if (this.inNestedEntity) {
      return;
    }

    const customProps = this.customProps;
    const propName = this.prop;
    let valueToSet = null;

    try {
      if (customProps) {
        customProps.forEach(function (prop) {
          const customPropName = propName + '.' + prop;
          //tslint:disable-next-line
          eval(
            'controller.' +
              customPropName +
              ' = ' +
              valueToSet +
              '; if(typeof (controller.' +
              customPropName +
              'Trigger) === "function"){ controller.' +
              customPropName +
              'Trigger()}'
          );
        });
      } else {
        if (this.controlType && this.controlType.match(/iterate-collection/)) {
          // Allow the trigger to do the reset
          return;
        } else if (this.controlType === 'checkbox-basic') {
          valueToSet = false;
        }

        // Eval used to take the property given, set it, and trigger an update if trigger exists
        //tslint:disable-next-line
        eval(
          'controller.' +
            propName +
            ' = ' +
            valueToSet +
            '; if(typeof (controller.' +
            propName +
            'Trigger) === "function"){ controller.' +
            propName +
            'Trigger()}'
        );
      }
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'MetadataNode',
        function: 'resetVal',
        category: UICategory.security,
        error: error,
      });
    }
  }

  resetLevel(): void {
    // For nodes in an iterate control, the level needs to be reset as
    // special handling in UI is done for the iterate tag
    this.level = 2;
    this.uiLevel = 0;
  }

  /*function find() { [native code] }*/

  find(prop: string): MetadataNode | null {
    if (this.isPropMatch(prop)) {
      return this;
    }

    const retChild = _.find(this.children, function (child) {
      return child.find(prop) !== null;
    });

    return retChild ? retChild.find(prop) : null;
  }

  findById(id: string): MetadataNode {
    UILoggerSingleton.getInstance().warn(
      {
        module: 'MetadataNode',
        function: 'findById',
        category: UICategory.security,
      },
      'Use of MetadataNode.findById() is deprecated, as tree traversal can lead to performance problems'
    );

    if (this.id === id) {
      return this;
    }

    const retChild = _.find(this.children, function (child) {
      return child.findById(id) !== null;
    });

    return retChild ? retChild.findById(id) : null;
  }

  findLevel1Node(): MetadataNode {
    // Find the parent level1 node
    if (this.level === 1) {
      return this;
    }

    let pNode = this.parent;

    while (pNode && pNode.level !== 1) {
      pNode = pNode.parent;
    }

    return pNode;
  }

  /**
   * Get the previous level 1 sibling by going to the parent node and getting the children.
   * If the current node happens to be the first child in a section, go to the previous section
   * @returns {MetadataNode} The prev level 1 node, or null.
   */

  findPrevLevel1Node(): MetadataNode | null {
    const level1Node = this.findLevel1Node();
    let i = _.indexOf(level1Node.parent.children, level1Node);

    if (i >= 0) {
      if (i === 0) {
        // Must be the first node in a section. Goto previous section and get last child
        const topLevelNode = level1Node.parent;
        let j = _.indexOf(topLevelNode.parent.children, topLevelNode);

        if (j > 0) {
          return getLastNode(topLevelNode.parent.children[--j]);
        }
      } else {
        return level1Node.parent.children[--i];
      }
    }

    return null;

    function getLastNode(node) {
      if (!node.topLevel) {
        return node;
      } else {
        return node.children[node.children.length - 1];
      }
    }
  }
}

@supertypeClass({ toClient: true, toServer: false })
export class NoneNode extends MetadataNode {
  constructor() {
    super();
  }

  static new(prop: string, options): NoneNode {
    return new NoneNode().init(prop, options);
  }

  init(prop: string, options): this {
    super.init(prop, options);
    return this;
  }
}

@supertypeClass({ toClient: true, toServer: false })
export class OtherNotSureNode extends MetadataNode {
  constructor() {
    super();
  }

  static new(prop: string, options): OtherNotSureNode {
    return new OtherNotSureNode().init(prop, options);
  }

  init(prop: string, options): this {
    super.init(prop, options);
    return this;
  }
}
